import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

/**
 * A multi-select dropdown with a built-in "All" option and exclusion-tracking
 * model.
 *
 * Behavior matrix (the value of `selectedIds` is internal, not exposed):
 *   - `[ALL]` + empty exclusions      -> every option is selected (no filter)
 *   - `[ALL]` + N exclusions          -> "All minus N" (the chip shows "N of M selected"
 *                                        or a single name if just one is selected)
 *   - explicit list (no ALL)          -> only those items are selected
 *   - empty                           -> nothing is selected
 *
 * Click semantics (matches a standard SaaS multi-select):
 *   - Click an unchecked item        -> select it (and re-check if it was excluded)
 *   - Click a checked item           -> deselect it
 *   - Click the All pill in the list -> toggle every option on/off as a group
 *   - Click the "x" on the chip      -> clear to empty (no filter rows match)
 *
 * The closed chip area always shows exactly one summary pill: "All" /
 * "<single name>" / "N of M selected" / nothing -- so the input doesn't
 * balloon with one chip per selected item when the option list is large.
 *
 * Consumer wires it up like this:
 *   <app-multi-select-with-all
 *     [options]="cmsOptions"
 *     idField="ContentManagementSystemId"
 *     labelField="Name"
 *     width="250px"
 *     panelMaxHeight="480px"
 *     (idsChange)="onCmsIds($event)">
 *   </app-multi-select-with-all>
 *
 * `idsChange` emits `number[] | null`: `null` = "no filter, send everything";
 * an explicit array = the ids to include. That's exactly the shape most
 * launchpad list APIs want, so the consumer can pass it straight through.
 */
@Component({
  selector: 'app-multi-select-with-all',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './multi-select-with-all.component.html',
  styleUrls: ['./multi-select-with-all.component.scss']
})
export class MultiSelectWithAllComponent implements OnChanges {
  @Input() options: any[] = [];
  @Input() idField = 'id';
  @Input() labelField = 'label';
  @Input() width = '250px';
  @Input() panelMaxHeight = '240px';
  @Input() searchable = true;
  @Input() searchFn: ((term: string, item: any) => boolean) | null = null;

  @Output() idsChange = new EventEmitter<number[] | null>();

  readonly ALL_TOKEN = '__ALL__';
  selectedIds: (number | string)[] = [this.ALL_TOKEN];
  excludedIds = new Set<number>();

  @HostBinding('style.--ms-panel-max-height') get cssMaxHeight(): string {
    return this.panelMaxHeight;
  }

  @HostBinding('style.width') get cssWidth(): string {
    return this.width;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      // If the option universe shrinks, drop exclusions that no longer apply
      // so the API call doesn't carry stale ids forward.
      const valid = new Set(this.allIds());
      for (const id of Array.from(this.excludedIds)) {
        if (!valid.has(id)) {
          this.excludedIds.delete(id);
        }
      }
    }
  }

  // Adapter so consumers can keep ng-select's two-argument searchFn shape.
  internalSearchFn = (term: string, item: any): boolean => {
    if (this.searchFn) {
      return this.searchFn(term, item);
    }
    const t = (term ?? '').toLowerCase().trim();
    if (!t) {
      return true;
    }
    if (item === this.ALL_TOKEN) {
      return 'all'.includes(t);
    }
    return this.getLabel(item).toLowerCase().includes(t);
  };

  getLabel(id: number | string): string {
    if (id === this.ALL_TOKEN) {
      return 'All';
    }
    const opt = this.options.find(o => o[this.idField] === id);
    return opt ? String(opt[this.labelField] ?? '') : '';
  }

  onChange(value: (number | string)[]): void {
    const allIds = this.allIds();
    const safeNext = value ?? [];
    const previous = this.selectedIds;
    const wasAll = previous.includes(this.ALL_TOKEN);
    const isAllNow = safeNext.includes(this.ALL_TOKEN);

    if (wasAll && isAllNow) {
      const added = safeNext.filter(v => v !== this.ALL_TOKEN && !previous.includes(v)) as number[];
      for (const id of added) {
        if (this.excludedIds.has(id)) {
          this.excludedIds.delete(id);
        } else {
          this.excludedIds.add(id);
        }
      }
      if (allIds.length > 0 && this.excludedIds.size === allIds.length) {
        this.excludedIds.clear();
        this.selectedIds = [];
        this.emit();
        return;
      }
      this.selectedIds = [this.ALL_TOKEN];
      this.emit();
      return;
    }

    if (wasAll && !isAllNow) {
      this.excludedIds.clear();
      this.selectedIds = [];
      this.emit();
      return;
    }

    if (!wasAll && isAllNow) {
      this.excludedIds.clear();
      this.selectedIds = [this.ALL_TOKEN];
      this.emit();
      return;
    }

    const explicit = safeNext.filter(v => v !== this.ALL_TOKEN);
    if (allIds.length > 0 && explicit.length === allIds.length) {
      this.excludedIds.clear();
      this.selectedIds = [this.ALL_TOKEN];
      this.emit();
      return;
    }
    this.selectedIds = explicit;
    this.emit();
  }

  onClear(): void {
    this.excludedIds.clear();
    this.selectedIds = [];
    this.emit();
  }

  get labelView(): MultiSelectLabelView {
    const allIds = this.allIds();
    const total = allIds.length;
    if (this.selectedIds.includes(this.ALL_TOKEN)) {
      if (this.excludedIds.size === 0) {
        return { kind: 'all', text: 'All' };
      }
      const selectedCount = total - this.excludedIds.size;
      if (selectedCount === 1) {
        const soloId = allIds.find(id => !this.excludedIds.has(id));
        return { kind: 'single', text: soloId !== undefined ? this.getLabel(soloId) : '' };
      }
      return { kind: 'count', text: `${selectedCount} of ${total} selected` };
    }
    const explicit = this.selectedIds.filter(v => v !== this.ALL_TOKEN) as number[];
    if (explicit.length === 0) {
      return { kind: 'empty', text: '' };
    }
    if (explicit.length === 1) {
      return { kind: 'single', text: this.getLabel(explicit[0]) };
    }
    return { kind: 'count', text: `${explicit.length} of ${total} selected` };
  }

  iconClass(value: number | string): string {
    if (this.selectedIds.includes(this.ALL_TOKEN)) {
      if (value === this.ALL_TOKEN) {
        return 'fas fa-check-square text-success';
      }
      return this.excludedIds.has(value as number)
        ? 'fas fa-stop-circle text-danger'
        : 'fas fa-check-square text-success';
    }
    return this.selectedIds.includes(value)
      ? 'fas fa-check-square text-success'
      : 'fas fa-stop-circle text-danger';
  }

  trackOption = (_: number, opt: any): number => opt[this.idField];

  private allIds(): number[] {
    return this.options.map(o => o[this.idField]);
  }

  private emit(): void {
    const allIds = this.allIds();
    let ids: number[] | null;
    if (this.selectedIds.includes(this.ALL_TOKEN)) {
      ids = this.excludedIds.size === 0
        ? null
        : allIds.filter(id => !this.excludedIds.has(id));
    } else {
      ids = this.selectedIds as number[];
    }
    this.idsChange.emit(ids);
  }
}

export interface MultiSelectLabelView {
  kind: 'all' | 'single' | 'count' | 'empty';
  text: string;
}
